import { Image, Button } from 'react-bootstrap'
import { IoDocumentOutline } from 'react-icons/io5'
import { useMutation } from 'react-query'

import { numberFormatCurrency } from '../../utils/helpers'
import { getCarInfo } from '../../utils/invoice'
import { useApi, useLoading } from '../../hooks'
import toast from '../toast'
import { VEHICLE_STATUS } from '../../core/constants1'
import ServicesInfo from './ServicesInfo'
import VehiclePhotos from './VehiclePhotos'
import ImgVehicleMobile from '../../assets/images/car/vehicle-desktop.svg'
import DontWorkVehicleIcon from '../../assets/images/icons/dont-work-vehicle.svg'
import ApproveForWork from '../../assets/images/icons/approve-for-work.svg'

const VehicleItem = ({
  vehicle,
  index,
  record = {},
  onShowPhoto,
  isShowSupplement,
  isShowParts,
  showEstAdasChecklists,
  showInvAdasChecklists,
  handleApproveOrRejectVehicle,
  isEstimateDetail = false,
  isShowButtonApproveOrReject = false,
  showScans = true,
  isWorkOrder = false,
  assignedTo = [],
}) => {
  const { currency = 'usd' } = record
  const { image, year = '', vin = '', stock = '', total = 0, serviceLines = {}, status } = vehicle
  const isRejectVehicle = status === VEHICLE_STATUS.DECLINED && isEstimateDetail
  const isApprovedVehicle = status === VEHICLE_STATUS.APPROVED && isEstimateDetail

  const { pdfService } = useApi()
  const { isLoading, mutate: downloadPdf } = useMutation(({ recordId, type }) => {
    const services = (type === 'parts') ? pdfService.estimatePartsPDF : (type === 'supplement') ? pdfService.estimateSupplementPDF : ((type === 'adas_estimate')) ? pdfService.estimateADASPDF : pdfService.invoiceADASPDF
    return services({ recordId }, false)
  }, {
    onError: error => toast.error({ title: 'View PDF errors', body: error.message })
  })

  useLoading(isLoading)

  function pad(d) {
    return (d < 10) ? '0' + d.toString() : d.toString()
  }

  const assignedName = assignedTo.length > 0
    ? assignedTo.map(a => a.user?.name || a.name).filter(Boolean).join(', ')
    : ''

  return (
    <div className='document-vehicle-card estimate-detail-vehicle-info'>
      {isRejectVehicle && <div className='reject-vehicle rounded-top' />}
      {isApprovedVehicle && <div className='approved-vehicle rounded-top' />}
      <div className={`p-3 ${isRejectVehicle && 'position-relative'}`}>
        {isRejectVehicle && <div className='blur' />}
        <div className='d-flex'>
          {!isWorkOrder && <span className='d-inlone-block fw-bold'>{pad(Number(index) + 1)}</span>}
          <div style={{ width: '200px', height: '104px' }}>
            <Image
              src={image
                ? (image.startsWith('http://') || image.startsWith('https://') ? image : image.replace('/color/', '/colorCropped/'))
                : ImgVehicleMobile}
              className='w-100 h-100'
              style={{ objectFit: image ? 'cover' : 'contain', objectPosition: (image && !image.startsWith('http://') && !image.startsWith('https://')) ? 'center top' : 'center center' }}
              onError={(e) => { e.target.onerror = null; e.target.src = ImgVehicleMobile }}
            />
          </div>
          <div className='px-3 flex-grow-1'>
            {year && <span className='d-block'>{year}</span>}
            <h5 className='fw-bold'>{getCarInfo(vehicle)}</h5>
            <span className='d-block vehicle-stock-vin'>STOCK/RO: {stock || 'Not listed'}</span>
            <span className='d-block vehicle-stock-vin'>VIN: {vin || 'Not listed'}</span>
            {isWorkOrder && serviceLines?.repairedBy && (
              <span className='d-block vehicle-stock-vin'>Repaired By: {serviceLines.repairedBy.replace(/^Repaired By:\s*/i, '')}</span>
            )}
            {isWorkOrder && assignedName && (
              <span className='d-block vehicle-stock-vin'>Assigned to: {assignedName}</span>
            )}
          </div>
          <div className='text-end'>
            {isRejectVehicle && <div className='btn-reject'>Rejected</div>}
            {isApprovedVehicle && <div className='btn-approved'>Approved</div>}
            {!isWorkOrder && <h5 className='fw-bold'>{numberFormatCurrency(currency, total)}</h5>}
            {isShowSupplement && (
              <Button
                variant='default'
                onClick={() => downloadPdf({ recordId: record._id, type: 'supplement' })}
                className='text-primary p-0 fw-bold fs-sub text-uppercase'
              >
                View insurance supplement
                <IoDocumentOutline size={16} className='ms-2' />
              </Button>
            )}
            {isShowSupplement && <br />}
            {isShowParts && (
              <Button
                variant='default'
                onClick={() => downloadPdf({ recordId: record._id, type: 'parts' })}
                className='text-primary p-0 fw-bold fs-sub text-uppercase'
              >
                Parts List
                <IoDocumentOutline size={16} className='ms-2' />
              </Button>
            )}
            {showEstAdasChecklists && (
              <Button
                variant='default'
                onClick={() => downloadPdf({ recordId: record._id, type: 'adas_estimate' })}
                className='text-primary p-0 fw-bold fs-sub text-uppercase'
              >
                ADAS Checklists
                <IoDocumentOutline size={16} className='ms-2' />
              </Button>
            )}
            {showInvAdasChecklists && (
              <Button
                variant='default'
                onClick={() => downloadPdf({ recordId: record._id, type: 'adas_invoice' })}
                className='text-primary p-0 fw-bold fs-sub text-uppercase'
              >
                ADAS Checklists
                <IoDocumentOutline size={16} className='ms-2' />
              </Button>
            )}
          </div>
        </div>

        <ServicesInfo serviceLines={serviceLines} hideAmounts={isWorkOrder} />

        {isShowButtonApproveOrReject && (
          <div>
            <div className={`${status === VEHICLE_STATUS.DECLINED ? 'border-top' : 'border-top border-bottom'}`}>
              {status === VEHICLE_STATUS.UNDECIDED && (
                <div className='d-flex justify-content-around approve-or-reject-desktop'>
                  <Button
                    variant='white'
                    className='d-flex align-items-center'
                    onClick={() => handleApproveOrRejectVehicle({ ...vehicle, status: VEHICLE_STATUS.APPROVED })}
                  >
                    <span className='mr-1 text-primary'>APPROVE FOR WORK</span>
                    <Image src={ApproveForWork} />
                  </Button>
                  <Button
                    className='d-flex align-items-center'
                    onClick={() => handleApproveOrRejectVehicle({ ...vehicle, status: VEHICLE_STATUS.DECLINED })}
                    variant='white'
                  >
                    <span className='mr-1 text-reject-vehicle'>DON'T WORK ON THIS VEHICLE</span>
                    <Image src={DontWorkVehicleIcon} />
                  </Button>
                </div>
              )}
              {status === VEHICLE_STATUS.APPROVED && (
                <div className='d-flex justify-content-end'>
                  <Button
                    className='d-flex align-items-center'
                    onClick={() => handleApproveOrRejectVehicle({ ...vehicle, status: VEHICLE_STATUS.DECLINED })}
                    variant='white'
                  >
                    <span className='mr-1 text-reject-vehicle'>DON'T WORK ON THIS VEHICLE</span>
                    <Image src={DontWorkVehicleIcon} />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {!isShowButtonApproveOrReject && !isWorkOrder && <div className='border-bottom' />}

        <VehiclePhotos vehicle={vehicle} showScans={showScans} />
      </div>

      {isShowButtonApproveOrReject && status === VEHICLE_STATUS.DECLINED && (
        <div className='d-flex justify-content-center pb-2'>
          <Button
            variant='white'
            className='d-flex align-items-center'
            onClick={() => handleApproveOrRejectVehicle({ ...vehicle, status: VEHICLE_STATUS.APPROVED })}
          >
            <span className='mr-1 text-primary'>APPROVE FOR WORK</span>
            <Image src={ApproveForWork} />
          </Button>
        </div>
      )}
    </div>
  )
}

export default VehicleItem
